import React from "react";
import OnboardingStepHeader from "./OnboardingStepHeader";
import OnboardingSidePanel from "./OnboardingSidePanel";

interface OnboardingScreenProps {
  showHeader?: boolean;
  showBack?: boolean;
  progress?: number | null;
  /** Gap under the header: 28px default, 4px on scroll-paged screens. */
  headerBottom?: number;
  headerAccessory?: React.ReactNode;
  onBack?: () => void;
  footer?: React.ReactNode;
  children: React.ReactNode;
  /**
   * `grow` (default): page grows with content. `scroll`: viewport-height
   * screen, the whole content area scrolls under a pinned header/footer.
   * `fixed`: viewport-height screen where a child (e.g. the sports list)
   * manages its own scrolling. `scrollDesktop`: `grow` on phones; from `lg`
   * up the step column scrolls beside the side panel.
   */
  layout?: "grow" | "scroll" | "fixed" | "scrollDesktop";
  /** Shorthand for `layout="scroll"`. */
  scroll?: boolean;
  /**
   * Column width. `phone` (480px) matches iOS; `medium` (720px on desktop)
   * for dense forms; `wide` (max-w-5xl) for the plan cards. `wide` screens
   * drop the desktop side panel so the cards get the room.
   */
  width?: "phone" | "medium" | "wide";
  /** Legacy shorthand for `width="wide"`. */
  wide?: boolean;
  className?: string;
}

const COLUMN_WIDTH: Record<NonNullable<OnboardingScreenProps["width"]>, string> = {
  phone: "max-w-[480px]",
  medium: "max-w-[480px] lg:max-w-[720px]",
  wide: "max-w-5xl",
};

/**
 * iOS `OnboardingScreen`: bg-primary, 24px sides, 16px top, 24px bottom,
 * header row, flexible content, footer pinned to the bottom.
 *
 * Phones get the iOS layout verbatim. From `lg` up the screen becomes a split
 * view: brand/video side panel on the left, the step column vertically
 * centred on the right (capped height so long lists scroll under a pinned
 * footer instead of stretching the page).
 */
const OnboardingScreen: React.FC<OnboardingScreenProps> = ({
  showHeader = true,
  showBack = true,
  progress = null,
  headerBottom = 28,
  headerAccessory,
  onBack,
  footer,
  children,
  layout,
  scroll = false,
  width,
  wide = false,
  className = "",
}) => {
  const mode = layout ?? (scroll ? "scroll" : "grow");
  const fixedHeight = mode === "scroll" || mode === "fixed";
  const desktopScroll = mode === "scrollDesktop";
  const columnWidth = width ?? (wide ? "wide" : "phone");
  const showSidePanel = columnWidth !== "wide";

  return (
    <div
      className={`flex w-full bg-peak-bg text-peak-text ${
        fixedHeight ? "h-[100dvh] overflow-hidden" : desktopScroll ? "min-h-[100dvh] lg:h-[100dvh] lg:overflow-hidden" : "min-h-[100dvh]"
      } ${className}`}
    >
      {showSidePanel && <OnboardingSidePanel />}
      <main className="flex min-h-0 w-full flex-1 flex-col lg:items-center lg:justify-center lg:px-12 lg:py-10">
        <div
          className={`mx-auto flex min-h-0 w-full flex-1 flex-col px-6 pb-6 pt-4 lg:flex-none lg:px-0 lg:pb-0 lg:pt-0 ${COLUMN_WIDTH[columnWidth]} ${
            fixedHeight || desktopScroll ? "lg:max-h-[min(860px,calc(100dvh-5rem))]" : "lg:min-h-[min(560px,calc(100dvh-5rem))]"
          }`}
        >
          {showHeader && (
            <div className="shrink-0" style={{ paddingBottom: headerBottom }}>
              <OnboardingStepHeader
                showBack={showBack}
                progress={progress}
                accessory={headerAccessory}
                onBack={onBack}
                hideBrandOnDesktop={showSidePanel}
              />
            </div>
          )}
          <div
            className={`flex flex-1 flex-col ${fixedHeight ? "min-h-0" : ""} ${mode === "scroll" ? "overflow-y-auto" : ""} ${
              desktopScroll ? "lg:-mr-4 lg:min-h-0 lg:overflow-y-auto lg:pr-4" : ""
            }`}
          >
            {children}
          </div>
          {footer && <div className="shrink-0 pt-6">{footer}</div>}
        </div>
      </main>
    </div>
  );
};

export default OnboardingScreen;

/** Title block: Funnel Bold 32 (tracking −0.6) + optional Funnel 17 lead. */
export const OnboardingTitle: React.FC<{ title: React.ReactNode; subtitle?: React.ReactNode; center?: boolean }> = ({
  title,
  subtitle,
  center = false,
}) => (
  <div className={`flex flex-col gap-3 ${center ? "items-center text-center" : ""}`}>
    <h1 className="font-funnel text-[32px] font-bold leading-[1.1] tracking-[-0.6px] text-peak-text lg:text-[36px]">{title}</h1>
    {subtitle && <p className="font-funnel text-[17px] leading-snug text-peak-text-secondary">{subtitle}</p>}
  </div>
);

export const OnboardingErrorText: React.FC<{ children?: React.ReactNode }> = ({ children }) =>
  children ? <p className="font-funnel text-[14px] text-peak-error" role="alert">{children}</p> : null;
